import { Checkbox, Chip, List, ListItemButton, ListItemIcon, ListItemText, Stack, TextField } from '@mui/material'
import { Banner, useFormDialog } from '@if/mui'
import { Merchant } from '@src/types/Merchant'
import { Control, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMerchantsFilter } from './SelectMerchantsDialog-hooks'

interface MerchantsForm {
  merchants: Merchant[]
}

const SelectMerchantsFields = ({
  control,
  merchantsTotal,
  label,
}: {
  control: Control<MerchantsForm>
  merchantsTotal: Merchant[]
  label: string
}) => {
  const { t } = useTranslation()
  const { field } = useController({ control, name: 'merchants' })
  const selected = field.value ?? []
  const { filteredMerchants, query, setQuery } = useMerchantsFilter(merchantsTotal)

  const toggle = (merchant: Merchant, isChecked: boolean) =>
    field.onChange(isChecked ? [...selected, merchant] : selected.filter((m) => m.id !== merchant.id))

  return (
    <Stack spacing={2}>
      <TextField id="queryField" value={query} onChange={(e) => setQuery(e.target.value)} label={label} fullWidth />
      {query?.trim() && filteredMerchants.length === 0 && (
        <Banner severity="error">{t('common.searchYieldsNoResults')}</Banner>
      )}
      {query === '' && <Banner severity="info">{t('common.typeToStartSearch')}</Banner>}
      {filteredMerchants.length > 0 && (
        <List>
          {filteredMerchants.map((merchant) => (
            <ListItemButton
              key={merchant.id}
              onClick={() => toggle(merchant, !selected.some((m) => m.id === merchant.id))}
            >
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={selected.some((m) => m.id === merchant.id)}
                  tabIndex={-1}
                  onChange={(e) => toggle(merchant, e.target.checked)}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText primary={merchant.name} />
            </ListItemButton>
          ))}
        </List>
      )}
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {selected.map((merchant: Merchant) => (
          <Chip key={merchant.id} label={merchant.name} size="small" onDelete={() => toggle(merchant, false)} />
        ))}
      </Stack>
    </Stack>
  )
}

export const useSelectMerchantsDialog = (merchantsTotal: Merchant[], label: string) => {
  const { t } = useTranslation()
  return useFormDialog<MerchantsForm, Merchant[]>({
    title: t('worksheet.addMerchantPayment'),
    confirmLabel: t('common.confirm'),
    defaultValues: (selected) => ({ merchants: selected ?? [] }),
    content: ({ form }) => (
      <SelectMerchantsFields control={form.control} merchantsTotal={merchantsTotal} label={label} />
    ),
  })
}
